import { parseDate, normalizePhone, combineName } from './coerce';

const TEXT_FIELDS = ['email', 'type', 'EHR_ID', 'physician_name', 'time_'];

function cellAt(row, index) {
  if (index == null) return null;
  const v = row[index];
  return v == null ? null : v;
}

function text(value) {
  if (value == null) return '';
  return String(value).trim();
}

// A column mapping is either a single header index (legacy) or a combine spec
// { sources: [idx...], separator }. isMapped tells whether anything is selected;
// resolveCell returns the value: a single source keeps its raw type (Date/number
// so date parsing still works), multiple sources join as trimmed text.
function isMapped(spec) {
  if (spec == null) return false;
  if (typeof spec === 'number') return true;
  return Array.isArray(spec.sources) && spec.sources.some((i) => i != null);
}

function resolveCell(raw, spec, defaultSep) {
  if (spec == null) return null;
  if (typeof spec === 'number') return cellAt(raw, spec);
  const idxs = (spec.sources || []).filter((i) => i != null);
  if (idxs.length === 0) return null;
  if (idxs.length === 1) return cellAt(raw, idxs[0]);
  return idxs.map((i) => text(cellAt(raw, i))).filter(Boolean).join(spec.separator || defaultSep || ' ');
}

export function transformRows({ rawRows, dataStartRowIndex, columnMap, nameFormat, transforms, defaultVisitDate, extraColumns = [] }) {
  const { parseDates, normalizePhones, onCoerceFail } = transforms;
  const rows = [];
  const stats = { total: 0, imported: 0, skippedNoName: 0, unparseable: { dob: 0, date_: 0, tel_num: 0, home_phone: 0 } };

  for (let i = dataStartRowIndex; i < rawRows.length; i += 1) {
    const raw = rawRows[i];
    if (!Array.isArray(raw)) continue;
    stats.total += 1;

    // Resolve patient name (explicit column wins over split parts). The
    // patient_name mapping may combine several source headers (e.g. first + last).
    let name = '';
    if (isMapped(columnMap.patient_name)) {
      name = text(resolveCell(raw, columnMap.patient_name, ' '));
    } else if (isMapped(columnMap.name_first) || isMapped(columnMap.name_middle) || isMapped(columnMap.name_last)) {
      name = combineName({
        first: text(resolveCell(raw, columnMap.name_first, ' ')),
        middle: text(resolveCell(raw, columnMap.name_middle, ' ')),
        last: text(resolveCell(raw, columnMap.name_last, ' ')),
      }, nameFormat);
    }
    if (!name) { stats.skippedNoName += 1; continue; }

    const out = { patient_name: name };

    TEXT_FIELDS.forEach((key) => {
      if (isMapped(columnMap[key])) {
        const t = text(resolveCell(raw, columnMap[key], ' '));
        if (t) out[key] = t;
      }
    });

    ['dob', 'date_'].forEach((key) => {
      if (!isMapped(columnMap[key])) return;
      const cell = resolveCell(raw, columnMap[key], ' ');
      if (cell == null || cell === '') return;
      const v = parseDates ? parseDate(cell, { onFail: onCoerceFail }) : (cell instanceof Date ? parseDate(cell) : text(cell));
      if (v) out[key] = v;
      else stats.unparseable[key] += 1;
    });

    ['tel_num', 'home_phone'].forEach((key) => {
      if (!isMapped(columnMap[key])) return;
      const cell = resolveCell(raw, columnMap[key], ' ');
      if (cell == null || cell === '') return;
      const v = normalizePhones ? normalizePhone(cell, { onFail: onCoerceFail }) : text(cell);
      if (v) out[key] = v;
      else stats.unparseable[key] += 1;
    });

    extraColumns.forEach((col) => {
      const parts = (col.sources || [])
        .map((idx) => text(cellAt(raw, idx)))
        .filter(Boolean);
      const val = parts.join(col.separator || ', ');
      if (val) out[col.name] = val;
    });

    // Optional: stamp a visit date on every imported row that doesn't already
    // have one (lets the user make imported patients show up under a day's schedule).
    if (defaultVisitDate && !out.date_) out.date_ = defaultVisitDate;

    rows.push(out);
    stats.imported += 1;
  }

  return { rows, stats };
}
